import React, { useEffect, useState } from 'react';
import './ShopPage.css';
import useShopLogic from '../logic/ShopLogic';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { handleBuy } from '../utils/shopUtils';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ShopPage = ({ collectionName = "shopItems-v2" }) => {
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmQuantity, setConfirmQuantity] = useState(1);
  const [dpShortage, setDpShortage] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const [dp, setDp] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [processingDots, setProcessingDots] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const unsubscribe = setInterval(() => {
      const user = getAuth().currentUser;
      if (!user) navigate('/');
    }, 1000);

    return () => clearInterval(unsubscribe);
  }, [navigate]);

  const {
    items,
    quantities,
    handleQuantityChange,
    handleBuyClick,
    pseudo,
  } = useShopLogic({ setConfirmItem, setConfirmQuantity, setDpShortage, setDp, dp, collectionName });


  useEffect(() => {
    if (!isConfirming) return;

    const interval = setInterval(() => {
      setProcessingDots((prev) => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, [isConfirming]);

  const confirmPurchase = () => {
    setIsConfirming(true);
    setProcessingDots('');

    if (!confirmItem) return;

    const userId = getAuth().currentUser?.uid;
    handleBuy(confirmItem, userId, confirmQuantity, () => {
      setPurchaseSuccess(true);
      setIsConfirming(false);
      setConfirmItem(null);
      setProcessingDots('');
    }, collectionName);
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      if (!confirmItem) return;

      const userId = getAuth().currentUser?.uid;
      await handleBuy(confirmItem, userId, confirmQuantity, () => {
        setPurchaseSuccess(true);
        setIsConfirming(false);
        setConfirmItem(null);
        setProcessingDots('');
        toast.success(t('shop.purchaseSuccessToast'));
      }, collectionName);
    } catch (error) {
      toast.error(t('shop.purchaseFailedToast'));
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="shop-container">
      <h1 className="shop-title">{t('shop.title')}</h1>

      <div className="shop-buttons">
        <button className="back-button" onClick={() => {
          playSound('return'); navigate('/shop')
        }}>{t('shop.back')}</button>
        <button className="nav-button" onClick={() => { navigate('/history'); }}>
          {t('shop.purchaseHistory')}
        </button>
      </div>

      <div className="user-info">
        ID: {pseudo || '...'}&nbsp;&nbsp;&nbsp;  {t('menu.dp')}: {dp !== null ? dp : '...'}
      </div>

      <div className="shop-scroll">
        {items.map((item) => (
          <div key={item.id} className="shop-item">
            <div className="clickable-image" onClick={() => setSelectedItem(item)}>
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={item.imageUrl} alt={item.name} className="item-image" />
            </div>
            <div className="item-details">
              <div className="item-name">{item.name}</div>
              <div className="item-description">{item.description}</div>
              <div className="item-price">{item.priceDP} DP</div>
            </div>
            <div className="purchase-panel">
              <select
                className="quantity-dropdown"
                value={quantities[item.id] || 1}
                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
              >
                {Array.from({ length: item.stock }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
              <button
                className="buy-button"
                disabled={item.stock === 0 || purchasing}
                onClick={() => handleBuyClick(item)}
              >
                {item.stock === 0 ? t('shop.comingSoon') : t('shop.buy')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedItem && (
        <div className="item-modal" onClick={() => setSelectedItem(null)}>
          <div className="item-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="zoom-wrapper">
              <img
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={selectedItem.imageUrl} alt={selectedItem.name} />
            </div>
            <h3>{selectedItem.name}</h3>
            <p>{selectedItem.description}</p>
            <button onClick={() => setSelectedItem(null)}>{t('shop.close')}</button>
          </div>
        </div>
      )}

      {confirmItem && (
        <div className="confirmation-modal-shop" onClick={() => !isConfirming && setConfirmItem(null)}>
          <div className="confirmation-panel-shop" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="settings-title">{t('shop.confirmPurchase')}</div>
            </div>
            
            <div className="confirmation-item-preview">
              {confirmItem.imageUrl && (
                <div className="confirmation-item-image">
                  <img 
                    src={confirmItem.imageUrl} 
                    alt={confirmItem.name}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              <div className="confirmation-item-details">
                <div className="confirmation-item-name">{confirmItem.name}</div>
                <div className="confirmation-item-meta">
                  <div className="confirmation-quantity">
                    <span className="label">{t('shop.quantity')}:</span>
                    <span className="value">{confirmQuantity}</span>
                  </div>
                  <div className="confirmation-price">
                    <span className="label">{t('shop.unitPrice')}:</span>
                    <span className="value">{confirmItem.priceDP} DP</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="confirmation-divider"></div>

            <div className="confirmation-total">
              <span className="total-label">{t('shop.total')}:</span>
              <span className="total-value">{confirmItem.priceDP * confirmQuantity} DP</span>
            </div>

            <div className="confirmation-actions">
              <button
                className="confirm-button-shop"
                onClick={!isConfirming ? confirmPurchase : undefined}
                disabled={isConfirming || purchasing}
              >
                {isConfirming ? (
                  <>
                    {t('shop.processing')}...
                  </>
                ) : (
                  <>
                    ✓ {t('shop.confirm')}
                  </>
                )}
              </button>
              <button
                className="cancel-button-shop"
                onClick={!isConfirming ? () => setConfirmItem(null) : undefined}
                disabled={isConfirming}
              >
                ✕ {t('shop.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {dpShortage !== null && (
        <div className="confirmation-modal-shop">
          <div className="confirmation-panel-shop">
            <div className="settings-title">{t('shop.notEnoughDP')}</div>
            <p>
              {t('shop.needMoreDP')}{' '}
              <strong style={{ color: 'rgba(230,57,57,1)' }}>{dpShortage} {t('shop.moreDPText')}</strong> {t('shop.toBuyItem')}
            </p>
            <div className="confirmation-actions">
              <button className="nav-button confirm-button" onClick={() => setDpShortage(null)} >
                {t('shop.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {purchaseSuccess && (
        <div className="success-modal-shop">
          <div className="success-panel-shop">
            <div className="settings-title">{t('shop.purchaseSuccessful')}</div>
            <p>
              {t('shop.successfullyBought')}{' '}
              <strong style={{ color: 'rgba(230,57,57,1)' }}>
                {confirmQuantity}
              </strong>{' '}
              {confirmItem?.name}.
            </p>
            <div className="success-actions">
              <button
                className="success-ok-button"
                onClick={() => {
                  setPurchaseSuccess(false);
                  setConfirmItem(null);
                  setConfirmQuantity(1);
                  setDpShortage(null);
                }}
              >
                {t('shop.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
